import React, { useState } from 'react'

import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEraser } from '@fortawesome/sharp-regular-svg-icons'
import {
  ActionIcon,
  Button,
  Divider,
  Drawer,
  Flex,
  Grid,
  Group,
  Stack,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'

import DefaultPageExport from '../DefaultPageExport'
import FilterChips from '../FilterChips'
import { FilterControlbarProps } from './filter-control-bar'
import FulltextFilter from './FulltextFilter'
import LocalStorageFilterControl from './LocalStorageFilterControl'
import FilterRenderer from '../Filters/FilterRenderer'
import { DetailFilterDefinition } from '../hooks/useDebouncedFilterValue/use-debounced-filter-value'
import { getFulltextFilterDefinition } from '../hooks/useDebouncedFilterValue/utils'

const FilterControlbar = <T,>({
  filterDefinitions = [],
  sortFilter,
  fulltextFilterTextInputProps,
  withPageExport = true,
  tableInstance,
  actionIconSize = 'lg',
}: FilterControlbarProps<T>): React.ReactElement => {
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const fullTextFilterDefinition = getFulltextFilterDefinition({
    filterDefinitions,
  })
  const theme = useMantineTheme()

  const detailFilterDefinitions: DetailFilterDefinition[] =
    filterDefinitions.filter(
      (f) => ('type' in f && f.type !== 'fulltext') || 'component' in f,
    ) as DetailFilterDefinition[]

  const detailFilterDefinitionsLength = detailFilterDefinitions.length

  if (detailFilterDefinitionsLength === 0 && !fullTextFilterDefinition) {
    return <></>
  }

  return (
    <Stack py="xs">
      <Flex
        gap={theme.spacing.sm}
        justify={fullTextFilterDefinition ? 'flex-start' : 'flex-end'}
      >
        {fullTextFilterDefinition && (
          <FulltextFilter
            sortFilter={sortFilter}
            definition={fullTextFilterDefinition}
            tableInstance={tableInstance}
            fulltextFilterTextInputProps={{
              ...fulltextFilterTextInputProps,
            }}
          />
        )}

        <Group position="right">
          <Tooltip
            label={i18n.t(
              'btn_tt_reset_all_filters',
              'Alle Filter und Sortierungen zurücksetzen',
            )}
            // label={t('btn_tt_reset_all_filters')}
          >
            <div>
              <ActionIcon
                color={theme.primaryColor}
                variant="filled"
                size={actionIconSize}
                disabled={sortFilter.activeFilterEqualsInitialFilter}
                onClick={() => {
                  sortFilter.resetFilters()
                }}
              >
                <FontAwesomeIcon
                  icon={faEraser}
                  fontSize={theme.fontSizes[actionIconSize]}
                />
              </ActionIcon>
            </div>
          </Tooltip>

          {detailFilterDefinitionsLength > 0 && (
            <LocalStorageFilterControl
              sortFilter={sortFilter}
              actionIconSize={actionIconSize}
            />
          )}

          {detailFilterDefinitionsLength > 0 && (
            <Drawer
              position="right"
              opened={filterDrawerOpen}
              onClose={() => setFilterDrawerOpen(false)}
              // title={t('detail_filter_drawer_title')}
              title={i18n.t('detail_filter_drawer_title', 'Detailfilter')}
            >
              <Grid>
                {detailFilterDefinitions.map((fd, index) => (
                  <Grid.Col
                    key={`id-detail-filter-${
                      'component' in fd
                        ? `${index}-component`
                        : `${fd.type}-${index}`
                    }`}
                  >
                    <FilterRenderer
                      sortFilter={sortFilter}
                      definition={fd}
                    />
                  </Grid.Col>
                ))}

                <Grid.Col>
                  <Group position="right">
                    <Button
                      color="gray"
                      variant="light"
                      onClick={() => sortFilter.resetFilters()}
                    >
                      {/* {t('btn_reset_detail_filter')} */}
                      {i18n.t('btn_reset_detail_filter', 'Zurücksetzen')}
                    </Button>
                    <Button onClick={() => setFilterDrawerOpen(false)}>
                      {/* {t('btn_close_detail_filter')} */}
                      {i18n.t('btn_close_detail_filter', 'Schließen')}
                    </Button>
                  </Group>
                </Grid.Col>
              </Grid>
            </Drawer>
          )}
          {detailFilterDefinitionsLength > 0 && (
            <Tooltip
              label={i18n.t('open_detail_filter_tt', 'Detailfilter öffnen')}
              // label={t('open_detail_filter_tt')}
            >
              <ActionIcon
                color={theme.primaryColor}
                variant="filled"
                size={actionIconSize}
                onClick={() => setFilterDrawerOpen(true)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  fontSize={theme.fontSizes[actionIconSize]}
                />
              </ActionIcon>
            </Tooltip>
          )}

          {withPageExport && (
            <>
              <Divider orientation="vertical" />
              <DefaultPageExport
                tableInstance={tableInstance}
                actionIconSize={actionIconSize}
              />
            </>
          )}
        </Group>
      </Flex>
      <FilterChips
        filterDefinitions={filterDefinitions}
        sortFilter={sortFilter}
      />
    </Stack>
  )
}

export default FilterControlbar
